import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0B192C]";
const lbl = "text-xs font-semibold uppercase tracking-wider text-slate-500";

export default function CreateOrder() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: async () => (await api.get("/customers")).data });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });

  const [f, setF] = useState({
    order_number: `GS${Math.floor(10000 + Math.random() * 90000)}`,
    customer_id: "", delivery_date: new Date().toISOString().slice(0, 10), delivery_time: "10:30 AM",
    driver_id: "", pallets: 1, cartons: 4, total_items: 5,
    special_instructions: "", po_number: "", invoice_number: "", delivery_notes: "",
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (sendToDriver) => {
    if (!f.customer_id) return toast.error("Select a customer");
    setSaving(true);
    try {
      const payload = { ...f, driver_id: sendToDriver ? f.driver_id || null : null };
      ["pallets", "cartons", "total_items"].forEach((k) => (payload[k] = parseInt(payload[k] || 0)));
      const { data } = await api.post("/orders", payload);
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`Order #${data.order_number} created${data.driver_id ? " & sent to driver" : ""}`);
      navigate("/admin/orders");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-3xl animate-slide-up">
      <button onClick={() => navigate("/admin/orders")} className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500"><ArrowLeft size={18} /> Orders</button>
      <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Create Delivery Order</h1>

      <div className="mt-5 space-y-5 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Order Number</label><input data-testid="order-number-input" value={f.order_number} onChange={(e) => set("order_number", e.target.value)} className={field} /></div>
          <div>
            <label className={lbl}>Customer</label>
            <Select value={f.customer_id} onValueChange={(v) => set("customer_id", v)}>
              <SelectTrigger className="mt-1" data-testid="order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className={lbl}>Delivery Date</label><input type="date" data-testid="order-date-input" value={f.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Delivery Time</label><input data-testid="order-time-input" value={f.delivery_time} onChange={(e) => set("delivery_time", e.target.value)} className={field} placeholder="10:30 AM" /></div>
          <div className="sm:col-span-2">
            <label className={lbl}>Driver (optional — leave blank for pending)</label>
            <Select value={f.driver_id} onValueChange={(v) => set("driver_id", v)}>
              <SelectTrigger className="mt-1" data-testid="order-driver-select"><SelectValue placeholder="Assign later" /></SelectTrigger>
              <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div><label className={lbl}>Pallets</label><input type="number" min="0" data-testid="order-pallets-input" value={f.pallets} onChange={(e) => set("pallets", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Cartons</label><input type="number" min="0" data-testid="order-cartons-input" value={f.cartons} onChange={(e) => set("cartons", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Total Items</label><input type="number" min="0" data-testid="order-items-input" value={f.total_items} onChange={(e) => set("total_items", e.target.value)} className={field} /></div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Customer PO Number</label><input data-testid="order-po-input" value={f.po_number} onChange={(e) => set("po_number", e.target.value)} className={field} /></div>
          <div><label className={lbl}>Invoice Number</label><input data-testid="order-invoice-input" value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} className={field} /></div>
        </div>
        <div><label className={lbl}>Special Instructions</label><textarea data-testid="order-instructions-input" value={f.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} rows={2} className={field} /></div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button onClick={() => submit(false)} disabled={saving} data-testid="create-delivery-btn" className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#0B192C] py-3 font-bold text-[#0B192C] tap-shrink disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Create Delivery"}
          </button>
          <button onClick={() => submit(true)} disabled={saving} data-testid="send-to-driver-btn" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#FF6500] py-3 font-bold text-white tap-shrink hover:bg-[#E05A00] disabled:opacity-60">
            <Send size={18} /> Create & Send to Driver
          </button>
        </div>
      </div>
    </div>
  );
}
